import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  Briefcase, Loader2, Plus, Link2, Upload, Copy, Mail, BarChart3, X,
  DollarSign, Users, Inbox, Building2, ExternalLink, FileDown, Send, Radar,
  CheckCircle2, CircleDashed, Wand2,
} from "lucide-react";
import { sr } from "../../lib/sr";
import { auth } from "../../lib/api";
import PerformanceDashboard from "../../components/smartreach/PerformanceDashboard";

const money = (n) => "$" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const num = (n) => Number(n || 0).toLocaleString();

export default function AgencyPortfolio() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("create");
  const [form, setForm] = useState({ name: "", website: "", category: "" });
  const [linkEmail, setLinkEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [agencyName, setAgencyName] = useState("");
  const [accent, setAccent] = useState("#2563EB");
  const [autoReport, setAutoReport] = useState(false);
  const [perfRange, setPerfRange] = useState("30");
  const [saStatus, setSaStatus] = useState({ configured: false });
  const [saFor, setSaFor] = useState(null);
  const [saLoc, setSaLoc] = useState("");
  const [saCamp, setSaCamp] = useState("");
  const [saStudies, setSaStudies] = useState(null); // null=not loaded, []=none
  const [saBusy, setSaBusy] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertPick, setAlertPick] = useState({}); // trackerId -> tenantId (manual client choice)
  const [dsFilter, setDsFilter] = useState("all");
  const [perf, setPerf] = useState(null);        // { client, analytics }
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const fileRef = useRef(null);
  const isSuper = auth.getUser()?.role === "super_admin";

  const load = () => sr.agencyOverview().then((d) => {
    setData(d); setAgencyName(d.agency?.name || "");
    setAccent(d.agency?.accent_color || "#2563EB"); setAutoReport(!!d.agency?.auto_report);
  }).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (isSuper) sr.stackadaptStatus().then(setSaStatus).catch(() => {}); }, [isSuper]);

  const loadAlerts = () => sr.footfallAlerts().then((d) => setAlerts(d.alerts || [])).catch(() => setAlerts([]));
  useEffect(() => { if (isSuper && saStatus.configured) loadAlerts(); }, [isSuper, saStatus.configured]);

  const mapAlert = async (alert) => {
    const tenantId = alert.suggested_client?.tenant_id || alertPick[alert.tracker_id];
    if (!tenantId) { toast.error("Pick a client to map this study to"); return; }
    try {
      await sr.addClientStudy(tenantId, { tracker_id: alert.tracker_id, campaign_id: alert.campaign_id });
      toast.success("Footfall study mapped — this client now shows measured visits");
      await load(); await loadAlerts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not map study"); }
  };

  const dismissAlert = async (trackerId) => {
    setAlerts((a) => a.filter((x) => x.tracker_id !== trackerId));
    try { await sr.dismissFootfallAlert(trackerId); } catch { loadAlerts(); }
  };

  const saveSa = async () => {
    try {
      const r = await sr.linkClientStackAdapt(saFor.tenant_id, { location_set_ids: saLoc, campaign_ids: saCamp });
      toast.success((r.client.sa_location_set_ids || []).length ? "the ad platform mapping saved" : "the ad platform mapping cleared");
      setSaFor(null); await load(); loadAlerts();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const openSa = (client) => {
    setSaFor(client);
    setSaLoc((client.sa_location_set_ids || []).join(", "));
    setSaCamp((client.sa_campaign_ids || []).join(", "));
    setSaStudies(null);
    if (saStatus.configured) {
      sr.stackadaptFootfallStudies().then((d) => setSaStudies(d.studies || [])).catch(() => setSaStudies([]));
    }
  };

  const autoMap = async () => {
    setSaBusy(true);
    try {
      const r = await sr.autoMapClientStackAdapt(saFor.tenant_id);
      if (r.ok) {
        toast.success(r.message || "Auto-mapped");
        setSaLoc((r.client?.sa_location_set_ids || []).join(", "));
        setSaCamp((r.client?.sa_campaign_ids || []).join(", "));
        setSaFor(null); await load(); loadAlerts();
      } else {
        toast.message(r.message || "No matching footfall study found");
        if (r.studies) setSaStudies(r.studies);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not auto-map"); }
    finally { setSaBusy(false); }
  };

  const applyStudy = (study) => {
    setSaLoc((prev) => {
      const ids = prev.split(/[\s,]+/).filter(Boolean);
      if (study.tracker_id && !ids.includes(String(study.tracker_id))) ids.push(String(study.tracker_id));
      return ids.join(", ");
    });
    if (study.campaign_id) {
      setSaCamp((prev) => {
        const ids = prev.split(/[\s,]+/).filter(Boolean);
        if (!ids.includes(String(study.campaign_id))) ids.push(String(study.campaign_id));
        return ids.join(", ");
      });
    }
    toast.success("Study added — click Save mapping to apply");
  };

  const saveName = async () => {
    setSavingName(true);
    try { await sr.updateAgency({ name: agencyName, accent_color: accent }); toast.success("Agency brand saved"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingName(false); }
  };

  const toggleAuto = async () => {
    const next = !autoReport;
    setAutoReport(next);
    try { await sr.updateAgency({ auto_report: next }); toast.success(next ? "Monthly auto-reports ON" : "Monthly auto-reports OFF"); }
    catch { setAutoReport(!next); toast.error("Could not update"); }
  };

  const downloadPdf = async (client, range) => {
    try {
      const blob = await sr.clientReportPdf(client.tenant_id, range && range !== "30" ? range : null);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${client.name.replace(/\s+/g, "_")}_performance.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
  };

  const sendRecap = async (client) => {
    if (!client.invited_email) { toast.error("Invite the client first so we have an email to send to"); return; }
    try { const r = await sr.sendClientReport(client.tenant_id); toast.success(`Recap emailed to ${r.email}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send recap"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await sr.uploadAgencyLogo(file); toast.success("Logo updated"); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not upload logo"); }
  };

  const addClient = async () => {
    setBusy(true);
    try {
      if (mode === "create") {
        if (!form.name.trim()) { toast.error("Enter a client business name"); setBusy(false); return; }
        await sr.createAgencyClient(form);
        setForm({ name: "", website: "", category: "" });
      } else {
        if (!linkEmail.includes("@")) { toast.error("Enter the client owner's email"); setBusy(false); return; }
        await sr.linkAgencyClient(linkEmail.trim());
        setLinkEmail("");
      }
      toast.success("Client added");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add client"); }
    finally { setBusy(false); }
  };

  const copyShare = (url) => {
    if (!url) return;
    try {
      const p = navigator.clipboard?.writeText(url);
      if (p && p.catch) p.catch(() => {});
    } catch { /* clipboard unavailable */ }
    toast.success("Share link copied");
  };

  const viewPerf = async (client, range = "30") => {
    setPerfRange(range);
    setPerf({ client, analytics: null });
    try { const d = await sr.clientPerformance(client.tenant_id, range !== "30" ? range : null); setPerf({ client, ...d }); }
    catch { toast.error("Could not load performance"); setPerf(null); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await sr.inviteAgencyClient(inviteFor.tenant_id, inviteEmail.trim());
      setInviteResult(r);
      toast.success(r.emailed ? "Invite emailed to client" : "Client login created");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not invite"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load your agency portfolio.</p></div>;

  const t = data.totals || {};
  const clients = data.clients || [];
  const logo = data.agency?.logo_url;

  return (
    <div data-testid="agency-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Briefcase size={12} /> Agency Portfolio</span>
        <h1 style={{ fontSize: 30 }}>All your clients, one command center</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Track how every client is performing, then give each one a white-labeled dashboard branded with your agency logo.</p>
      </div>

      {/* Agency branding */}
      <div className="sr-card" style={{ marginBottom: 18 }} data-testid="agency-branding">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Your agency brand</div>
        <div style={{ display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ width: 84, height: 84, borderRadius: 12, border: "1px dashed var(--border)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "#fff" }}>
            {logo ? <img src={logo} alt="Agency logo" style={{ maxWidth: "100%", maxHeight: "100%" }} data-testid="agency-logo-preview" />
              : <Building2 size={28} style={{ color: "var(--border)" }} />}
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label className="sr-label" style={{ fontSize: 12 }}>Agency name</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="sr-input" value={agencyName} onChange={(e) => setAgencyName(e.target.value)} data-testid="agency-name-input" style={{ maxWidth: 320 }} />
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={saveName} disabled={savingName} data-testid="agency-name-save">
                {savingName ? <Loader2 size={14} className="sr-spin" /> : "Save"}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} style={{ display: "none" }} data-testid="agency-logo-input" />
            <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 10 }} onClick={() => fileRef.current?.click()} data-testid="agency-logo-upload">
              <Upload size={14} /> {logo ? "Replace logo" : "Upload logo"}
            </button>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>This logo appears on top of every shared client dashboard.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 200 }}>
            <div>
              <label className="sr-label" style={{ fontSize: 12 }}>Brand accent color</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="agency-accent-input"
                  style={{ width: 44, height: 34, border: "1px solid var(--border)", borderRadius: 8, padding: 2, cursor: "pointer" }} />
                <span style={{ fontFamily: "monospace", fontSize: 13, color: "var(--navy)" }}>{accent}</span>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={saveName} disabled={savingName} data-testid="agency-accent-save">Apply</button>
              </div>
              <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Used for charts &amp; highlights on client dashboards.</p>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} data-testid="agency-autoreport-toggle" onClick={toggleAuto}>
              <span style={{ width: 40, height: 22, borderRadius: 999, background: autoReport ? accent : "var(--border)", position: "relative", transition: "background .15s", flexShrink: 0 }}>
                <span style={{ position: "absolute", top: 2, left: autoReport ? 20 : 2, width: 18, height: 18, borderRadius: 999, background: "#fff", transition: "left .15s" }} />
              </span>
              <span style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>Email clients a monthly recap</span>
            </label>
          </div>
        </div>
      </div>

      {/* Roll-up totals */}
      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }} data-testid="agency-totals">
        <Stat label="Clients" value={t.clients} icon={Briefcase} />
        <Stat label="Combined spend" value={money(t.spend)} icon={DollarSign} accent />
        <Stat label="Total visits" value={num(t.visits)} icon={Users} />
        <Stat label="Total leads" value={num(t.leads)} icon={Inbox} />
        <Stat label="Blended cost / visit" value={t.cost_per_visit != null ? "$" + t.cost_per_visit : "—"} />
      </div>

      {/* Footfall Ready Alert */}
      {isSuper && alerts.length > 0 && (
        <div className="sr-card" style={{ marginBottom: 20, background: "#E7F6EC", border: "1px solid #B6E3C6" }} data-testid="footfall-alert-strip">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Radar size={16} style={{ color: "#067647" }} />
            <span style={{ fontWeight: 800, color: "#067647", fontSize: 15 }}>{alerts.length} footfall study{alerts.length > 1 ? "ies" : "y"} ready to map</span>
          </div>
          <p className="sr-muted" style={{ fontSize: 12, marginBottom: 12 }}>New footfall measurement {alerts.length > 1 ? "studies are" : "study is"} live. Map {alerts.length > 1 ? "them" : "it"} to a client to switch that dashboard to <b>measured</b> store visits.</p>
          <div style={{ display: "grid", gap: 8 }}>
            {alerts.map((a) => (
              <div key={a.tracker_id} data-testid={`footfall-alert-${a.tracker_id}`}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#fff", border: "1px solid #B6E3C6", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>{a.tracker_name || `Study ${a.tracker_id}`}</div>
                  <div className="sr-muted" style={{ fontSize: 12 }}>
                    {a.campaign_name ? `${a.campaign_name} · ` : ""}{a.visits != null ? `${num(a.visits)} visits` : "measuring"}
                    {a.visitation_lift != null ? ` · lift ${a.visitation_lift}%` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  {!a.suggested_client && (
                    <select className="sr-input" style={{ maxWidth: 200, padding: "6px 8px", fontSize: 13 }}
                      value={alertPick[a.tracker_id] || ""} onChange={(e) => setAlertPick((p) => ({ ...p, [a.tracker_id]: e.target.value }))}
                      data-testid={`footfall-alert-pick-${a.tracker_id}`}>
                      <option value="">Choose client…</option>
                      {clients.map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.name}</option>)}
                    </select>
                  )}
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => mapAlert(a)} data-testid={`footfall-alert-map-${a.tracker_id}`}>
                    <Radar size={13} /> {a.suggested_client ? `Map to ${a.suggested_client.name}` : "Map"}
                  </button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => dismissAlert(a.tracker_id)} data-testid={`footfall-alert-dismiss-${a.tracker_id}`}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add client */}
      <div className="sr-card" style={{ marginBottom: 20 }} data-testid="agency-add-client">
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <button className={`sr-btn sr-btn-sm ${mode === "create" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setMode("create")} data-testid="add-mode-create"><Plus size={14} /> New client business</button>
          <button className={`sr-btn sr-btn-sm ${mode === "link" ? "sr-btn-primary" : "sr-btn-ghost"}`} onClick={() => setMode("link")} data-testid="add-mode-link"><Link2 size={14} /> Link existing business</button>
        </div>
        {mode === "create" ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Business name" v={form.name} on={(v) => setForm((f) => ({ ...f, name: v }))} testid="client-name" w={220} />
            <Field label="Website (optional)" v={form.website} on={(v) => setForm((f) => ({ ...f, website: v }))} testid="client-website" w={200} />
            <Field label="Category (optional)" v={form.category} on={(v) => setForm((f) => ({ ...f, category: v }))} testid="client-category" w={180} />
            <button className="sr-btn sr-btn-primary" onClick={addClient} disabled={busy} data-testid="client-create-btn">
              {busy ? <Loader2 size={14} className="sr-spin" /> : <><Plus size={15} /> Add client</>}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="Client owner SmartReach email" v={linkEmail} on={setLinkEmail} testid="client-link-email" w={300} />
            <button className="sr-btn sr-btn-primary" onClick={addClient} disabled={busy} data-testid="client-link-btn">
              {busy ? <Loader2 size={14} className="sr-spin" /> : <><Link2 size={15} /> Link business</>}
            </button>
          </div>
        )}
      </div>

      {/* Client list */}
      <div className="sr-eyebrow" style={{ marginBottom: 12 }}>Clients</div>
      {clients.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }} data-testid="ds-filter">
          {[["all", "All"], ["measured", "Measured"], ["modeled", "Modeled"], ["representative", "Representative"]].map(([key, label]) => {
            const count = key === "all" ? clients.length : clients.filter((c) => (c.data_source || "modeled") === key).length;
            const active = dsFilter === key;
            return (
              <button key={key} onClick={() => setDsFilter(key)} data-testid={`ds-filter-${key}`}
                style={{
                  border: "1px solid " + (active ? accent : "var(--border)"), borderRadius: 999,
                  background: active ? accent : "#fff", color: active ? "#fff" : "var(--navy)",
                  fontSize: 12, fontWeight: 700, padding: "6px 12px", cursor: "pointer",
                }}>
                {label} <span style={{ opacity: 0.75 }}>· {count}</span>
              </button>
            );
          })}
        </div>
      )}
      {clients.length === 0 && <div className="sr-card" data-testid="agency-empty"><p className="sr-muted">No clients yet — add your first client business above.</p></div>}
      {clients.length > 0 && clients.filter((c) => dsFilter === "all" || (c.data_source || "modeled") === dsFilter).length === 0 && (
        <div className="sr-card" data-testid="ds-filter-empty"><p className="sr-muted">No clients on {dsFilter} data. <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setDsFilter("all")}>Show all</button></p></div>
      )}
      <div style={{ display: "grid", gap: 14 }}>
        {clients.filter((c) => dsFilter === "all" || (c.data_source || "modeled") === dsFilter).map((c) => (
          <div key={c.tenant_id} className="sr-card" data-testid={`agency-client-${c.tenant_id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ color: "var(--navy)", fontWeight: 800, fontSize: 17 }}>{c.name}</div>
                {c.website && <div className="sr-muted" style={{ fontSize: 12 }}>{c.website}</div>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <DataSourceBadge source={c.data_source} />
                <span className="sr-badge sr-badge-gray">{c.live_campaigns} live campaigns</span>
              </div>
            </div>
            <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(100px,1fr))", gap: 10, marginTop: 14 }}>
              <Metric label="Spend" value={money(c.spend)} />
              <Metric label="Impressions" value={num(c.impressions)} />
              <Metric label="Visits" value={num(c.visits)} />
              <Metric label="Leads" value={num(c.leads)} />
              <Metric label="Cost / visit" value={c.cost_per_visit != null ? "$" + c.cost_per_visit : "—"} highlight />
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => viewPerf(c, "30")} data-testid={`client-view-${c.tenant_id}`}><BarChart3 size={14} /> View performance</button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => downloadPdf(c)} data-testid={`client-pdf-${c.tenant_id}`}><FileDown size={14} /> Download PDF</button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copyShare(c.share_url)} data-testid={`client-share-${c.tenant_id}`}><Copy size={14} /> Copy share link</button>
              {c.share_url && <a className="sr-btn sr-btn-ghost sr-btn-sm" href={c.share_url} target="_blank" rel="noreferrer" data-testid={`client-open-${c.tenant_id}`}><ExternalLink size={14} /> Open</a>}
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setInviteFor(c); setInviteEmail(c.invited_email || ""); setInviteResult(null); }} data-testid={`client-invite-${c.tenant_id}`}><Mail size={14} /> {c.invited_email ? "Re-invite" : "Invite client"}</button>
              {c.invited_email && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => sendRecap(c)} data-testid={`client-recap-${c.tenant_id}`}><Send size={14} /> Send recap</button>}
              {isSuper && (
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => openSa(c)} data-testid={`client-sa-${c.tenant_id}`}>
                  <Radar size={14} /> the ad platform{c.data_source === "measured" ? " ✓" : ""}
                </button>
              )}
              {c.invited_email && <span className="sr-badge sr-badge-gray" style={{ alignSelf: "center" }}>Invited: {c.invited_email}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Performance modal */}
      {perf && (
        <div className="srx-modal-overlay" onClick={() => setPerf(null)} data-testid="perf-modal">
          <div className="srx-modal" style={{ maxWidth: 920, maxHeight: "90vh", overflowY: "auto", padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <button className="srx-modal-close" onClick={() => setPerf(null)} data-testid="perf-modal-close"><X size={20} /></button>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              {logo && <img src={logo} alt="" style={{ height: 34 }} />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, color: "var(--navy)", fontSize: 20 }}>{perf.client.name}</div>
                <div className="sr-muted" style={{ fontSize: 12 }}>Performance dashboard{data.agency?.name ? ` · ${data.agency.name}` : ""}</div>
              </div>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => downloadPdf(perf.client, perfRange)} data-testid="perf-modal-pdf"><FileDown size={14} /> PDF</button>
            </div>
            {perf.analytics
              ? <PerformanceDashboard analytics={perf.analytics} accent={perf.agency?.accent_color || accent} range={perfRange} onRangeChange={(r) => viewPerf(perf.client, r)} />
              : <div className="sr-empty"><Loader2 className="sr-spin" /></div>}
          </div>
        </div>
      )}

      {/* footfall measurement mapping modal */}
      {isSuper && saFor && (
        <div className="srx-modal-overlay" onClick={() => setSaFor(null)} data-testid="sa-modal">
          <div className="srx-modal" style={{ maxWidth: 560, maxHeight: "88vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <button className="srx-modal-close" onClick={() => setSaFor(null)} data-testid="sa-modal-close"><X size={20} /></button>
            <h3>Connect footfall measurement — {saFor.name}</h3>
            <p>Map this client to its footfall measurement study to show <b>measured</b> store visits instead of modeled estimates.{!saStatus.configured && " (Note: the ad platform API key isn't configured on the server yet — mapping is saved and activates once it is.)"}</p>

            {saStatus.configured && (
              <div style={{ marginTop: 14, padding: 12, border: "1px solid var(--border)", borderRadius: 10, background: "var(--blue-light)" }} data-testid="sa-automap-box">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Wand2 size={16} style={{ color: "var(--blue)" }} />
                    <span style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>Auto-map footfall</span>
                  </div>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={autoMap} disabled={saBusy} data-testid="sa-automap-btn">
                    {saBusy ? <Loader2 size={14} className="sr-spin" /> : <><Wand2 size={14} /> Auto-map</>}
                  </button>
                </div>
                <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  Finds the matching footfall study automatically from your synced ad platform campaigns and maps it in one click.
                </p>

                {saStudies === null && (
                  <div className="sr-muted" style={{ fontSize: 12, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                    <Loader2 size={12} className="sr-spin" /> Checking your account for live footfall studies…
                  </div>
                )}
                {saStudies !== null && saStudies.length === 0 && (
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="sa-studies-empty">
                    No footfall studies exist on this ad platform account yet. Once a campaign with a visit-tracking (footfall) study starts measuring, it will appear here for one-click mapping.
                  </p>
                )}
                {saStudies !== null && saStudies.length > 0 && (
                  <div style={{ marginTop: 10, display: "grid", gap: 8 }} data-testid="sa-studies-list">
                    <div className="sr-eyebrow" style={{ fontSize: 11 }}>Available footfall studies ({saStudies.length})</div>
                    {saStudies.map((s) => (
                      <div key={s.tracker_id} data-testid={`sa-study-${s.tracker_id}`}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, background: "#fff", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 10px" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.tracker_name || `Study ${s.tracker_id}`}</div>
                          <div className="sr-muted" style={{ fontSize: 11 }}>
                            {s.campaign_name ? `${s.campaign_name} · ` : ""}{s.summary?.footfall_conv != null ? `${num(s.summary.footfall_conv)} visits` : "no result yet"}
                          </div>
                        </div>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => applyStudy(s)} data-testid={`sa-use-study-${s.tracker_id}`}>Use</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: 14, display: "grid", gap: 12 }}>
              <div>
                <label className="sr-label" style={{ fontSize: 12 }}>Location set / tracker IDs (comma-separated)</label>
                <input className="srx-modal-input" value={saLoc} onChange={(e) => setSaLoc(e.target.value)} data-testid="sa-loc-input" placeholder="e.g. 10231, 10244" />
              </div>
              <div>
                <label className="sr-label" style={{ fontSize: 12 }}>Campaign IDs (optional)</label>
                <input className="srx-modal-input" value={saCamp} onChange={(e) => setSaCamp(e.target.value)} data-testid="sa-camp-input" placeholder="e.g. 462370" />
              </div>
              <button className="srx-btn-primary srx-full" onClick={saveSa} data-testid="sa-save-btn"><Radar size={15} /> Save mapping</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite modal */}
      {inviteFor && (
        <div className="srx-modal-overlay" onClick={() => setInviteFor(null)} data-testid="invite-modal">
          <div className="srx-modal" onClick={(e) => e.stopPropagation()}>
            <button className="srx-modal-close" onClick={() => setInviteFor(null)} data-testid="invite-modal-close"><X size={20} /></button>
            <h3>Invite {inviteFor.name}</h3>
            <p>Create a login so your client can sign in and view their branded performance dashboard. They can also use the share link with no login.</p>
            {!inviteResult ? (
              <div style={{ marginTop: 16 }}>
                <input className="srx-modal-input" placeholder="client@email.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
                <button className="srx-btn-primary srx-full" style={{ marginTop: 12 }} onClick={sendInvite} disabled={busy} data-testid="invite-send-btn">
                  {busy ? <><Loader2 size={15} className="sr-spin" /> Sending…</> : <><Mail size={15} /> Send invite</>}
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 16 }} data-testid="invite-result">
                <div className="sr-card" style={{ background: "#F1FBF4", border: "1px solid #B6E3C6" }}>
                  <p style={{ fontSize: 14, color: "#067647", fontWeight: 700 }}>{inviteResult.emailed ? "Invite emailed ✓" : "Login ready ✓"}</p>
                  <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>Email: <b>{inviteResult.email}</b></p>
                  {inviteResult.temp_password && <p className="sr-muted" style={{ fontSize: 13 }}>Temporary password: <b>{inviteResult.temp_password}</b> — share it securely; they can change it after signing in.</p>}
                  {!inviteResult.temp_password && <p className="sr-muted" style={{ fontSize: 13 }}>{inviteResult.note}</p>}
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>Sign-in: {inviteResult.login_url}</p>
                </div>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginTop: 12 }} onClick={() => setInviteFor(null)} data-testid="invite-done-btn">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const DS_BADGE = {
  measured: { label: "Measured", bg: "#E7F6EC", color: "#067647", Icon: CheckCircle2 },
  modeled: { label: "Modeled", bg: "#EEF1F0", color: "#5B6B85", Icon: CircleDashed },
  representative: { label: "Representative", bg: "#FCF1DC", color: "#9A7412", Icon: CircleDashed },
};

const DataSourceBadge = ({ source }) => {
  const b = DS_BADGE[source] || DS_BADGE.modeled;
  return (
    <span className="sr-badge" style={{ background: b.bg, color: b.color, display: "inline-flex", alignItems: "center", gap: 4 }}
      title={source === "measured" ? "Measured footfall visits" : "Modeled estimate — map footfall measurement for measured visits"}
      data-testid={`client-data-source-${source || "modeled"}`}>
      <b.Icon size={11} /> {b.label}
    </span>
  );
};

const Field = ({ label, v, on, testid, w }) => (
  <div className="sr-field" style={{ margin: 0 }}>
    <label className="sr-label" style={{ fontSize: 12 }}>{label}</label>
    <input className="sr-input" style={{ width: w }} value={v} onChange={(e) => on(e.target.value)} data-testid={testid} />
  </div>
);

const Stat = ({ label, value, accent, icon: Icon }) => (
  <div className="sr-card" style={accent ? { background: "var(--blue-light)", border: "1px solid var(--blue)" } : {}}>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {Icon && <Icon size={13} style={{ color: "var(--blue)" }} />}
      <span className="sr-muted" style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: 24, fontWeight: 800, color: "var(--navy)", marginTop: 4 }}>{value ?? "—"}</div>
  </div>
);

const Metric = ({ label, value, highlight }) => (
  <div style={highlight ? { background: "#F1FBF4", borderRadius: 10, padding: "8px 10px" } : {}}>
    <div className="sr-muted" style={{ fontSize: 11, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: highlight ? "#067647" : "var(--navy)", marginTop: 2 }}>{value}</div>
  </div>
);
